import classNames from "classnames";
import { type PropsWithChildren, useContext } from "react";

import { SpinnerLoader } from "components/loader/loader";
import { PageHeader } from "components/page-header";
import { AuthAppContext } from "components/root/auth-app-provider";
import { Notifications } from "components/shared/notifications";
import { type NotificationName } from "components/shared/notifications/notifications";
import { PageFooter } from "components/shared/page-footer";
import { useFlags } from "utils";

import styles from "./base-layout.module.scss";

type BaseLayoutProps = PropsWithChildren<{
  title?: string;
  hideFooter?: boolean;
  hideHeader?: boolean;
  hideNavigation?: boolean;
  hidePolicyHeader?: boolean;
  tabs?: Record<string, string>[];
  loading?: boolean;
  availableNotifications?: NotificationName[];
}>;

const BaseLayout = ({
  children,
  hideFooter = false,
  hideHeader = false,
  hideNavigation = false,
  hidePolicyHeader = false,
  tabs = [],
  loading = false,
  availableNotifications = [],
}: BaseLayoutProps) => {
  const { isPolicyDataReady } = useContext(AuthAppContext);
  const { policyPage: isPolicyPageEnabled } = useFlags();
  // When policy page is on we show PolicyHeader row; when off we show multi-policy-selector in nav (handled in header) — hide PolicyHeader when flag is off
  const policyHeaderDisplay = !!(!isPolicyPageEnabled || hidePolicyHeader);
  const hasTabs = tabs && tabs?.length > 0;

  return (
    <>
      {!hideHeader ? (
        <PageHeader tabs={tabs} hidePolicyHeader={policyHeaderDisplay} />
      ) : null}
      <div
        className={classNames(
          styles.pageContainer,
          hideNavigation && styles.hideNavigation,
          hideHeader && styles.hideHeader,
          hideFooter && styles.hideFooter,
          !policyHeaderDisplay && styles.showPolicyHeader,
          !policyHeaderDisplay && hasTabs && styles.hasTabs
        )}
      >
        {availableNotifications.length ? (
          <Notifications availableNotifications={availableNotifications} />
        ) : null}
        {!isPolicyDataReady || loading ? (
          <div className={styles.spinnerWrapper}>
            <SpinnerLoader />
          </div>
        ) : (
          children
        )}
      </div>
      {!hideFooter ? (
        <PageFooter
          className={
            // fnol-policyholder component on claims submit page has a styling conflict with our footer on mobile
            location.pathname === "/my/claims/submit"
              ? styles.hideFooterMobile
              : ""
          }
        />
      ) : null}
    </>
  );
};

export { BaseLayout };
